import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, Target, DollarSign, Users, Award, Calendar, TrendingUp, Info } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function CampaignDetails() {
  const urlParams = new URLSearchParams(window.location.search);
  const campaignId = urlParams.get('id');
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({ participant_name:'', participant_email:'', participant_phone:'', amount:'' });
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: async () => { const campaigns = await base44.entities.Campaign.list(); return campaigns.find(c => c.id === campaignId); },
    enabled: !!campaignId
  });

  const createContributionMutation = useMutation({
    mutationFn: async (contributionData) => {
      const contribution = await base44.entities.Contribution.create(contributionData);
      await base44.entities.Campaign.update(campaign.id, { raised_amount: (campaign.raised_amount || 0) + parseFloat(contributionData.amount) });
      return contribution;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaign', campaignId]);
      setShowForm(false);
      setFormData({ participant_name:'', participant_email:'', participant_phone:'', amount:'' });
      alert('Votre participation a été enregistrée avec succès !');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(formData.amount);
    if (amount < campaign.min_contribution) { setError(`Le montant minimum est de ${campaign.min_contribution} XOF`); return; }
    const reward_m2 = (amount / 1000) * campaign.reward_per_1000;
    createContributionMutation.mutate({ campaign_id:campaignId, ...formData, amount, reward_m2, payment_status:'en_attente', transaction_ref:`TXN-${Date.now()}` });
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black"></div></div>;
  if (!campaign) return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><p className="text-xl text-gray-600 mb-4">Campagne non trouvée</p><Link to="/prefinancements"><button className="accent-gradient text-white px-6 py-2 rounded-lg">Retour</button></Link></div></div>;

  const progress = (campaign.raised_amount / campaign.target_amount) * 100;
  const daysRemaining = campaign.end_date ? Math.ceil((new Date(campaign.end_date) - new Date()) / (1000*60*60*24)) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/prefinancements"><button className="mb-6 bg-white/10 text-white border border-white/20 px-4 py-2 rounded-lg hover:bg-white/20 flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Retour aux campagnes</button></Link>
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }}>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{campaign.title}</h1>
            {campaign.start_date && campaign.end_date && (
              <div className="flex items-center gap-2 text-gray-200"><Calendar className="w-5 h-5" /><span>{format(new Date(campaign.start_date),'dd MMMM',{locale:fr})} - {format(new Date(campaign.end_date),'dd MMMM yyyy',{locale:fr})}</span></div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {campaign.featured_image && <div className="overflow-hidden shadow-xl rounded-xl"><img src={campaign.featured_image} alt={campaign.title} className="w-full h-96 object-cover" /></div>}
              {campaign.images?.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  {campaign.images.map((image, index) => (
                    <div key={index} className="overflow-hidden shadow-lg rounded-xl"><img src={image} alt={`${campaign.title} ${index + 1}`} className="w-full h-32 object-cover hover:scale-110 transition-transform duration-300" /></div>
                  ))}
                </div>
              )}
              <div className="border-none shadow-xl p-8 rounded-xl bg-white">
                <h2 className="text-2xl font-bold text-black mb-4">À propos de cette campagne</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{campaign.description}</p>
              </div>
              <div className="border-none shadow-xl bg-blue-50 p-8 rounded-xl">
                <div className="flex items-start gap-3">
                  <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Comment fonctionnent les récompenses ?</h3>
                    <p className="text-gray-700 leading-relaxed">Pour chaque tranche de 1000 XOF investie, vous recevrez <strong>{campaign.reward_per_1000} m²</strong> de terrain. Par exemple, si vous investissez 100 000 XOF, vous recevrez <strong>{(100000/1000)*campaign.reward_per_1000} m²</strong>.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border-none shadow-xl p-6 rounded-xl bg-white">
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-2xl font-bold text-black">{campaign.raised_amount?.toLocaleString()} XOF</span>
                    <span className="text-sm text-gray-600">{progress.toFixed(0)}%</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">sur {campaign.target_amount?.toLocaleString()} XOF</p>
                  <div className="w-full bg-gray-200 rounded-full h-3"><div className="accent-gradient h-3 rounded-full" style={{ width:`${Math.min(progress,100)}%` }}></div></div>
                </div>
                <div className="space-y-4">
                  {[{icon:Target,label:"Objectif",value:`${campaign.target_amount?.toLocaleString()} XOF`,bg:"bg-gray-50"},{icon:Users,label:"Minimum",value:`${campaign.min_contribution?.toLocaleString()} XOF`,bg:"bg-gray-50"},{icon:Award,label:"Récompense",value:`${campaign.reward_per_1000} m² / 1000 XOF`,bg:"bg-yellow-50"}].map((item,i) => (
                    <div key={i} className={`flex items-center justify-between p-3 ${item.bg} rounded-lg`}>
                      <div className="flex items-center gap-2"><item.icon className="w-5 h-5 text-black" /><span className="text-sm text-gray-600">{item.label}</span></div>
                      <span className="font-bold text-black">{item.value}</span>
                    </div>
                  ))}
                  {daysRemaining !== null && daysRemaining > 0 && campaign.status === 'active' && (
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2"><Calendar className="w-5 h-5 text-green-600" /><span className="text-sm text-gray-600">Temps restant</span></div>
                      <span className="font-bold text-green-600">{daysRemaining} jours</span>
                    </div>
                  )}
                </div>
                {campaign.status === 'active' && !showForm && (
                  <button className="w-full mt-6 accent-gradient text-white py-4 text-lg rounded-lg hover:opacity-90 flex items-center justify-center gap-2" onClick={() => setShowForm(true)}>
                    <TrendingUp className="w-5 h-5" />Participer Maintenant
                  </button>
                )}
              </div>

              {showForm && campaign.status === 'active' && (
                <div className="border-none shadow-xl p-6 rounded-xl bg-white">
                  <h3 className="text-xl font-bold text-black mb-4">Formulaire de Participation</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}
                    {[{label:"Nom complet *",id:"name",type:"text",field:"participant_name",placeholder:"Votre nom complet"},{label:"Email *",id:"email",type:"email",field:"participant_email",placeholder:"votre@email.com"},{label:"Téléphone *",id:"phone",type:"tel",field:"participant_phone",placeholder:"+225 0585868767"}].map(f => (
                      <div key={f.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                        <input type={f.type} required value={formData[f.field]} onChange={e => setFormData({...formData,[f.field]:e.target.value})} placeholder={f.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Montant (XOF) *</label>
                      <input type="number" required min={campaign.min_contribution} value={formData.amount} onChange={e => setFormData({...formData,amount:e.target.value})} placeholder={`Minimum ${campaign.min_contribution} XOF`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                      {formData.amount && parseFloat(formData.amount) >= campaign.min_contribution && (
                        <p className="text-sm text-green-600 mt-1">Vous recevrez environ {((parseFloat(formData.amount)/1000)*campaign.reward_per_1000).toFixed(2)} m²</p>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => { setShowForm(false); setError(''); }} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50">Annuler</button>
                      <button type="submit" disabled={createContributionMutation.isPending} className="flex-1 accent-gradient text-white py-2 rounded-lg hover:opacity-90">
                        {createContributionMutation.isPending ? 'En cours...' : 'Confirmer'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {campaign.status !== 'active' && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-blue-700 text-sm">Cette campagne n'est pas active actuellement.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
